"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { parseLineList } from "@/lib/tool-kit/study/line-list";

type GroupMode = "count" | "size";

function fisherYatesShuffle(items: string[]): string[] {
  const shuffled = items.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function splitIntoGroups(names: string[], groupCount: number): string[][] {
  const shuffled = fisherYatesShuffle(names);
  const groups: string[][] = [];
  const baseSize = Math.floor(shuffled.length / groupCount);
  const remainder = shuffled.length % groupCount;
  let cursor = 0;
  for (let i = 0; i < groupCount; i++) {
    const size = i < remainder ? baseSize + 1 : baseSize;
    groups.push(shuffled.slice(cursor, cursor + size));
    cursor += size;
  }
  return groups;
}

function makeGroups(names: string[], mode: GroupMode, n: number): string[][] {
  if (names.length === 0 || n < 1) {
    return [];
  }
  if (mode === "count") {
    return splitIntoGroups(names, Math.min(n, names.length));
  }
  const groupCount = Math.max(1, Math.ceil(names.length / n));
  return splitIntoGroups(names, groupCount);
}

function groupsToText(groups: string[][], groupLabel: string): string {
  return groups
    .map(
      (group, index) =>
        `${t(groupLabel, { number: index + 1 })}\n${group.join("\n")}`
    )
    .join("\n\n");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const GROUP_GENERATOR_STRINGS = {
  namesInputLabel: "Names — one per line",
  inputPlaceholder: "Alice\nBen\nChloe\nDiego\nEmma\nFinn",
  namesCount: "one:{count} name|other:{count} names",
  duplicatesRemoved:
    "one: {count} duplicate removed|other: {count} duplicates removed",
  splitBy: "Split by",
  numberOfGroups: "Number of groups",
  groupSize: "Group size",
  groups: "Groups",
  peoplePerGroup: "People per group",
  regenerate: "Regenerate",
  generateGroups: "Generate Groups",
  copied: "Copied!",
  copyAll: "Copy All",
  clear: "Clear",
  groupLabel: "Group {number}",
  names: "Names",
  largestGroup: "Largest group",
} as const;

export default function GroupGeneratorWidget() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<GroupMode>("count");
  const [n, setN] = useState(2);
  const [groups, setGroups] = useState<string[][]>([]);
  const [copied, setCopied] = useState(false);

  const locale = useToolLocale();
  const strings = useToolUi(GROUP_GENERATOR_STRINGS);

  const { items: names, removed: duplicates } = parseLineList(input);
  const largestGroup =
    groups.length > 0 ? Math.max(...groups.map((g) => g.length)) : 0;

  function handleGenerate() {
    setGroups(makeGroups(names, mode, n));
    setCopied(false);
  }

  function handleCopyAll() {
    if (groups.length === 0) {
      return;
    }
    writeToClipboard(groupsToText(groups, strings.groupLabel)).then(
      (copied) => {
        if (!copied) {
          return;
        }

        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    );
  }

  function handleClear() {
    setInput("");
    setGroups([]);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="group-input"
        >
          {strings.namesInputLabel}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="group-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(strings.namesCount, locale, names.length)}
        </p>
        {duplicates > 0 && (
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.duplicatesRemoved, locale, duplicates, {
              count: duplicates,
            })}
          </p>
        )}
      </div>

      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.splitBy}</p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "count"}
              className="accent-primary"
              name="group-mode"
              onChange={() => setMode("count")}
              type="radio"
              value="count"
            />
            {strings.numberOfGroups}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "size"}
              className="accent-primary"
              name="group-mode"
              onChange={() => setMode("size")}
              type="radio"
              value="size"
            />
            {strings.groupSize}
          </label>
          <label
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm"
            htmlFor="group-n"
          >
            {mode === "count" ? strings.groups : strings.peoplePerGroup}
            <input
              className="w-16 rounded border border-border bg-background px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="group-n"
              max={100}
              min={1}
              onChange={(e) =>
                setN(Math.max(1, parseIntegerInput(e.target.value) ?? 1))
              }
              type="number"
              value={n}
            />
          </label>
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={names.length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {groups.length > 0 ? strings.regenerate : strings.generateGroups}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={groups.length === 0}
          onClick={handleCopyAll}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && groups.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Groups */}
      {groups.length > 0 && (
        <section className="grid gap-4 sm:grid-cols-2">
          {groups.map((group, index) => (
            <div
              className="rounded-lg border border-border bg-card p-5"
              key={`group-${String(index)}`}
            >
              <p className="font-medium text-foreground">
                {t(strings.groupLabel, { number: index + 1 })}{" "}
                <span className="font-normal text-muted-foreground text-sm">
                  ({group.length})
                </span>
              </p>
              <ul className="mt-2 space-y-1 text-muted-foreground text-sm leading-relaxed">
                {group.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {/* Stats */}
      {groups.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.names} value={names.length} />
          <StatCard label={strings.groups} value={groups.length} />
          <StatCard label={strings.largestGroup} value={largestGroup} />
        </div>
      )}
    </>
  );
}
